import * as R from 'remeda';
import { getIDFromUrl, Url } from '@rossum/api-client';
import {
  DatapointData,
  MultivalueData,
  SectionData,
  TupleData,
} from '@rossum/api-client/annotations';
import { AutomationBlocker } from '@rossum/api-client/internal';
import { QueueAction } from '@rossum/api-client/schemas';
import { Message } from '@rossum/api-client/shared';
import { GridColDef } from '@rossum/ui/x-data-grid-pro';

export const columnsPrefix = 'columns';

export const globals = 'globals';

export const documentsColumn = 'document';

export const conditionsColumn = 'conditions';

export const actionsColumn = 'actions';

export type DatapointColumn = `${typeof columnsPrefix}.${string}`;

export type RowModel = {
  id: number | string;
  annotationUrl: Url;
  [documentsColumn]: string | undefined;
  [conditionsColumn]: boolean | undefined;
  [actionsColumn]: QueueAction[] | undefined;
  [globals]: {
    messages: Message[] | undefined;
    automationBlockers: AutomationBlocker[] | undefined;
  };
  isFetching: boolean;
} & Record<
  DatapointColumn,
  | {
      datapoints: Array<DatapointValue>;
      messages: Record<string, Message[]>;
      automationBlockers: Record<string, AutomationBlocker[]>;
      preview: string;
    }
  | undefined
>;

type DatapointValue = SectionData | DatapointData | TupleData | MultivalueData;

const getMaxLength = (arr: Array<Array<unknown>>) =>
  // at least one line per annotation should be displayed
  Math.max(1, ...arr.map(a => a.length));

// n + 1 for "...X more"
const MAX_ROWS_COUNT = 4;

const findBySchemaId = <
  Base extends { schemaId: string | null },
  C extends (Base & { children: C[] }) | Base,
>(
  content: Array<C>,
  schemaId: string
): Array<C> =>
  content.reduce<Array<C>>(
    (results, dp) =>
      dp.schemaId === schemaId
        ? [...results, dp]
        : 'children' in dp
          ? [...results, ...findBySchemaId(dp.children, schemaId)]
          : results,
    []
  );

export const getDatapointsData = (
  annotationContent: Array<SectionData> | undefined,
  datapointColumns: Array<GridColDef<RowModel>>,
  prefixForColumns: string
): SectionData[][] =>
  datapointColumns.flatMap(({ field }) =>
    annotationContent
      ? [
          findBySchemaId(
            annotationContent,
            field.replace(`${prefixForColumns}.`, '')
          ),
        ]
      : []
  );

type MergedConditions = Record<
  number,
  { condition: boolean; actions: QueueAction[] }
>;

const mergeConditionsAndActions = (
  conditions: boolean[],
  actions: QueueAction[]
): MergedConditions => {
  // Do this with mutations because it's more readable - but it means you have to copy the array
  const actionsCopy = actions.slice(0);

  // Use the fact that every matched condition has the same number of actions
  const conditionLength = conditions.filter(R.isTruthy).length;
  const count = conditionLength ? actions.length / conditionLength : 0;
  const result: MergedConditions = {};

  for (let index = 0; index < MAX_ROWS_COUNT - 1; index += 1) {
    // Can be both false (not matched) and undefined (loop runs for fixed number of lines but the annotation can have less)
    const condition = conditions[index];

    if (condition) {
      // For each truthy condition, take {count} first actions.
      // This expects actions we get from the B/E resource to be ordered
      const removed = actionsCopy.splice(0, count);
      result[index] = { condition, actions: removed };
    }
  }

  return result;
};

export const constructRows = <
  T extends {
    actions?: QueueAction[];
    conditionValues?: Array<Array<boolean>>;
  },
>({
  annotationUrl,
  document,
  documentsMap,
  datapointColumns,
  datapointsData,
  evaluationData,
  isFetching,
  messagesMap = {},
  automationBlockersMap = {},
  previewMap = {},
}: {
  annotationUrl: string;
  document: string;
  documentsMap: Record<string, string>;
  datapointColumns: Array<GridColDef<RowModel>>;
  datapointsData: DatapointValue[][];
  evaluationData: T | undefined;
  isFetching: boolean;
  messagesMap?: Record<string, Message[]>;
  automationBlockersMap?: Record<string, AutomationBlocker[]>;
  previewMap?: Record<string, string>;
}): Array<RowModel> => {
  const firstRuleConditions = evaluationData?.conditionValues?.[0];

  const conditionsAndActions = mergeConditionsAndActions(
    firstRuleConditions ?? [],
    evaluationData?.actions ?? []
  );

  return Array.from(
    { length: Math.min(getMaxLength(datapointsData), MAX_ROWS_COUNT) },
    (_, index) => {
      const isFirstRow = index === 0;

      const condition = conditionsAndActions[index]?.condition ?? false;
      const actions = conditionsAndActions[index]?.actions ?? [];

      const indexBasedAttributes = isFirstRow
        ? {
            [documentsColumn]: documentsMap[document],
            [actionsColumn]: actions,
            [conditionsColumn]: condition,
            [globals]: {
              messages: messagesMap?.['all'] || [],
              automationBlockers: automationBlockersMap?.['all'] || [],
            },
          }
        : {
            [documentsColumn]: '',
            [actionsColumn]: actions,
            [conditionsColumn]: condition,
            [globals]: {
              messages: [],
              automationBlockers: [],
            },
          };

      return {
        id:
          annotationUrl === 'preview'
            ? 'line-preview'
            : `${getIDFromUrl(annotationUrl)}-${index}`,
        annotationUrl,
        ...indexBasedAttributes,
        isFetching,
        ...datapointColumns.reduce((acc, column, columnIndex) => {
          const columnData = datapointsData[columnIndex];

          const isOverflowRow =
            index === MAX_ROWS_COUNT - 1 &&
            (columnData?.length ?? 0) > MAX_ROWS_COUNT - 1;

          const datapoints = columnData?.length
            ? R.pipe(
                isOverflowRow
                  ? columnData?.slice(MAX_ROWS_COUNT - 1)
                  : [columnData?.[index] ?? columnData?.[0]],
                R.filter(R.isTruthy)
              )
            : [];

          const datapointsIds = datapoints?.map(({ id }) => String(id)) ?? [];

          return {
            ...acc,
            [column.field]: datapoints
              ? {
                  preview:
                    annotationUrl === 'preview'
                      ? (previewMap[column.field.replace('columns.', '')] ?? '')
                      : '',
                  datapoints,
                  messages: R.pick(messagesMap, datapointsIds),
                  automationBlockers: R.pick(
                    automationBlockersMap,
                    datapointsIds
                  ),
                }
              : undefined,
          };
        }, {}),
      };
    }
  );
};
